import { competitorProfiles, lifeShackProfile } from "@/src/seo/competitors";
import { FREE_TRIAL_APPLICATION_CREDITS } from "@src/config/freeTrial";

export type ComparisonFeature = {
  feature: string;
  competitor: string;
  lifeshack: string;
};

export type ComparisonFaq = {
  question: string;
  answer: string;
};

export type ComparisonPage = {
  slug: string;
  relatedComparisonSlugs?: string[];
  competitor: string;
  title: string;
  metaDescription: string;
  eyebrow: string;
  headline: string;
  subheadline: string;
  competitorSummary: string;
  lifeshackSummary: string;
  competitorBestFor: string;
  lifeshackBestFor: string;
  competitorLimitation?: string;
  lifeshackLimitation?: string;
  verdict: string;
  competitorStrengths: string[];
  lifeshackAdvantages: string[];
  features: ComparisonFeature[];
  faqs: ComparisonFaq[];
  pricingNote?: string;
  reviewedAt?: string;
  sources?: {
    label: string;
    href: string;
  }[];
  analysisSections?: {
    heading: string;
    body: string[];
  }[];
};

const baseComparisonPages: ComparisonPage[] = [
  {
    slug: "jobright",
    competitor: "Jobright",
    title: "LifeShack vs Jobright | AI Job Search Copilot Comparison",
    metaDescription:
      "Compare LifeShack and Jobright for AI job matching, autofill, tailored resumes, referrals, auto apply, and job-search automation.",
    eyebrow: "LifeShack vs Jobright",
    headline: "Jobright helps you discover better-fit roles. LifeShack helps keep applications moving.",
    subheadline:
      "Jobright is an AI job-search copilot for matching, autofill, tracking, and referral discovery. LifeShack is built for job seekers who want the application workflow itself handled with tailored materials and clear controls.",
    competitorSummary:
      "Jobright focuses on AI job matching, application autofill, resume tailoring, job tracking, alerts, and insider connection recommendations. It helps most when discovery and prioritization are the main bottlenecks.",
    lifeshackSummary:
      "LifeShack focuses on moving from preferences to submitted applications: find relevant roles, tailor materials, submit applications, and track progress with controls like pause and company blocklists.",
    competitorBestFor:
      "Job seekers who mostly need job discovery, match scores, autofill, and referral context before applying.",
    lifeshackBestFor:
      "Job seekers who already know they need more applications submitted and want an AI agent to reduce repetitive application work.",
    verdict:
      "If discovery is the bottleneck, Jobright may help. If applications are not getting completed consistently, LifeShack is the better fit.",
    competitorStrengths: [
      "Personalized job matching and match scores.",
      "Application autofill and job tracking.",
      "Referral and insider-connection discovery.",
    ],
    lifeshackAdvantages: [
      "Focuses on the full application workflow after job preferences are set.",
      "Pairs auto apply with tailored resumes and cover letters.",
      "Gives users controls like pause/resume and company blocklists.",
    ],
    features: [
      {
        feature: "Core job",
        competitor: "Match jobs, autofill applications, tailor resumes, and surface referral context.",
        lifeshack: "Find, tailor, submit, and track applications through an AI job-search agent.",
      },
      {
        feature: "Job discovery",
        competitor: "Personalized feed and fit-scoring workflow.",
        lifeshack: "Search preferences guide which jobs the agent should pursue.",
      },
      {
        feature: "Application execution",
        competitor: "Autofill helps speed up applications the user chooses to pursue.",
        lifeshack: "LifeShack is built around reducing the repetitive submission workload.",
      },
      {
        feature: "Control",
        competitor: "User chooses roles, tracks status, and uses match/referral context.",
        lifeshack: "User sets preferences, pauses the agent, and blocks companies when needed.",
      },
      {
        feature: "Decision point",
        competitor: "Best when role discovery and prioritization are the main pain.",
        lifeshack: "Best when getting applications submitted is the main pain.",
      },
    ],
    faqs: [
      {
        question: "Is LifeShack a Jobright alternative?",
        answer:
          "Yes, for job seekers comparing AI job-search copilots. Jobright leans toward discovery, match scoring, autofill, and referrals. LifeShack is the better fit when you want the application workflow to keep moving after setup.",
      },
      {
        question: "Who should choose LifeShack over Jobright?",
        answer:
          "Choose LifeShack if you already have a defined target role and want more relevant applications submitted with less repetitive work. Jobright fits better when the main need is job matching and discovery.",
      },
    ],
  },
  {
    slug: "aiapply",
    competitor: "AIApply",
    title: "LifeShack vs AIApply | AI Auto Apply and Resume Tool Comparison",
    metaDescription:
      "Compare LifeShack and AIApply for AI auto apply, resume builders, cover letters, interview tools, tailored applications, and job-search automation.",
    eyebrow: "LifeShack vs AIApply",
    headline: "AIApply covers the job-search toolkit. LifeShack focuses on submitted applications.",
    subheadline:
      "AIApply covers resumes, cover letters, auto apply, interview practice, and coaching. LifeShack is built around the core outcome of getting relevant applications moving with clear user controls.",
    competitorSummary:
      "AIApply brings several AI job-search tools into one place: resume building, cover-letter generation, auto apply, interview practice, and coaching. That breadth can be helpful, but it also means auto apply is one part of a larger toolkit.",
    lifeshackSummary:
      "LifeShack narrows the experience around the application pipeline: set preferences, tailor materials, submit applications, track progress, and stay in control of where applications go.",
    competitorBestFor:
      "Job seekers who primarily want an all-in-one AI toolkit for resumes, cover letters, interviews, and applying.",
    lifeshackBestFor:
      "Job seekers who want a focused agentic workflow for getting more relevant applications submitted.",
    verdict:
      "If you want a broad job-search toolkit, AIApply may help. If your priority is a focused application engine that reduces the busywork between finding jobs and submitting them, LifeShack is the better fit.",
    competitorStrengths: [
      "AI resume, cover-letter, and interview tools.",
      "Auto-apply features within a larger toolkit.",
      "Preparation and applying features in one product.",
    ],
    lifeshackAdvantages: [
      "Keeps the product promise centered on application execution.",
      "Connects tailored materials directly to the submission workflow.",
      "Includes guardrails like pause controls and company blocklists.",
    ],
    features: [
      {
        feature: "Core job",
        competitor: "Resume tools, cover letters, interview practice, coaching, and auto apply.",
        lifeshack: "Find, tailor, submit, and track applications through a focused AI workflow.",
      },
      {
        feature: "Preparation tools",
        competitor: "Broad coverage across writing and interview support.",
        lifeshack: "Application materials are tied directly to roles being submitted.",
      },
      {
        feature: "Application execution",
        competitor: "Auto apply is one feature within a larger toolkit.",
        lifeshack: "Auto apply is the center of the workflow.",
      },
      {
        feature: "Control",
        competitor: "User manages a broader set of tools and outputs.",
        lifeshack: "User guides the agent with preferences, pause/resume, and company blocklists.",
      },
      {
        feature: "Decision point",
        competitor: "Best when you want a wide AI job-search toolbox.",
        lifeshack: "Best when you want the application pipeline to move with less manual work.",
      },
    ],
    faqs: [
      {
        question: "Is LifeShack an AIApply alternative?",
        answer:
          "Yes. Both products help job seekers use AI across applications. AIApply is broader across resumes, interviews, and coaching. LifeShack is more focused on getting applications tailored, submitted, and tracked.",
      },
      {
        question: "Who should choose LifeShack over AIApply?",
        answer:
          "Choose LifeShack if you want less of a general toolkit and more of a focused application workflow. It is built for job seekers who want applications moving, not just more documents to manage.",
      },
    ],
  },
  {
    slug: "lazyapply",
    competitor: "LazyApply",
    title: "LifeShack vs LazyApply | AI Job Application Bot Comparison",
    metaDescription:
      "Compare LifeShack and LazyApply for AI job application automation, free trials, tailored applications, tracking, and job-search control.",
    eyebrow: "LifeShack vs LazyApply",
    headline: "Automation is not enough. The applications need to be targeted, controlled, and measurable.",
    subheadline:
      "LazyApply is known for high-volume browser automation. LifeShack is built for job seekers who want a more guided workflow with free application credits, tailored materials, and controls around where applications go.",
    competitorSummary:
      "LazyApply positions itself around one-click, high-volume job application automation across job boards. It fits users who care most about raw application volume.",
    lifeshackSummary:
      "LifeShack is built to combine volume with quality controls: search preferences, tailored materials, application tracking, pause controls, and company blocklists.",
    competitorBestFor: "Job seekers who want a browser bot focused on sending many applications quickly.",
    lifeshackBestFor: "Job seekers who want automation but still care about fit, control, trial experience, and application quality.",
    verdict:
      "If your only goal is maximum automated volume, LazyApply may fit. If you want volume with personalization, visibility, and control, LifeShack is the better fit.",
    competitorStrengths: [
      "High-volume automation promise.",
      "Repeated form-filling across job boards.",
      "Volume-first approach for manual-application burnout.",
    ],
    lifeshackAdvantages: [
      `Free trial includes ${FREE_TRIAL_APPLICATION_CREDITS} application credits before committing to a paid plan.`,
      "Tailored resumes and cover letters connect directly to the application workflow.",
      "Company blocklists help prevent risky applications to current or sensitive employers.",
    ],
    features: [
      {
        feature: "Core job",
        competitor: "Automate large batches of job applications.",
        lifeshack: "Run a managed application workflow with matching, tailoring, submission, and tracking.",
      },
      {
        feature: "Trial path",
        competitor: "LazyApply says it does not offer a free trial; users can request a live demo.",
        lifeshack: `Free trial includes ${FREE_TRIAL_APPLICATION_CREDITS} application credits with a no-credit-card message on the homepage.`,
      },
      {
        feature: "Personalization",
        competitor: "Profile-based automation and application answers.",
        lifeshack: "Tailored resume and cover-letter options tied to job preferences.",
      },
      {
        feature: "Risk control",
        competitor: "Focused on application volume.",
        lifeshack: "Pause applications, edit preferences, and block companies.",
      },
      {
        feature: "Decision point",
        competitor: "Apply to many jobs in one click.",
        lifeshack: "Try the agent, watch applications go out, then upgrade when value is visible.",
      },
    ],
    faqs: [
      {
        question: "Is LifeShack safer than a bulk job application bot?",
        answer:
          "LifeShack is designed around control features such as pausing, search preferences, and blocklists. That makes the pitch less about blind volume and more about managed automation.",
      },
      {
        question: "What is the main LifeShack advantage over LazyApply?",
        answer:
          "LifeShack's advantage is the quality-at-scale workflow: tailored materials, tracked applications, and user controls before someone commits to a paid plan.",
      },
    ],
  },
  {
    slug: "jobcopilot",
    competitor: "JobCopilot",
    title: "LifeShack vs JobCopilot | AI Job Application Automation Comparison",
    metaDescription:
      "Compare LifeShack and JobCopilot for AI job application automation, auto apply, tailored applications, tracking, and application control.",
    eyebrow: "LifeShack vs JobCopilot",
    headline: "Automated applications can help. The difference is how much control you keep.",
    subheadline:
      "JobCopilot is built around a set-it-up-and-let-it-run AI copilot for job applications. LifeShack gives job seekers a more controlled application workflow with tailored materials, tracking, and clear guardrails.",
    competitorSummary:
      "JobCopilot emphasizes automated applications after a one-time setup: define target roles, upload a resume, answer screening questions, and let the copilot apply to relevant jobs.",
    lifeshackSummary:
      "LifeShack is built for job seekers who want automation with visibility: tailored materials, application tracking, pause controls, and company blocklists.",
    competitorBestFor:
      "Job seekers who want a background copilot to find and apply to jobs after setup.",
    lifeshackBestFor:
      "Job seekers who want application automation but still care about where applications go and how materials are tailored.",
    verdict:
      "If you want a background application copilot, JobCopilot may fit. If you want automation with tailored materials, visibility, and user control, LifeShack is the better fit.",
    competitorStrengths: [
      "Automated application promise.",
      "Setup flow for target roles and screening answers.",
      "Background application volume.",
    ],
    lifeshackAdvantages: [
      "Emphasizes controlled automation, not just background volume.",
      "Pairs applications with tailored resumes and cover letters.",
      "Lets users pause, adjust preferences, and block companies.",
    ],
    features: [
      {
        feature: "Core job",
        competitor: "Automatically find and apply to relevant jobs after setup.",
        lifeshack: "Find, tailor, submit, and track applications with user-guided controls.",
      },
      {
        feature: "Setup",
        competitor: "Define target jobs, upload a resume, and answer screening questions.",
        lifeshack: "Set preferences and let the workflow tailor and submit relevant applications.",
      },
      {
        feature: "Application quality",
        competitor: "Personalized applications based on setup data.",
        lifeshack: "Tailored resumes and cover letters tied to the application workflow.",
      },
      {
        feature: "Control",
        competitor: "Background copilot model.",
        lifeshack: "Pause/resume, dashboard tracking, and company blocklists.",
      },
      {
        feature: "Decision point",
        competitor: "Best when you want a copilot applying in the background.",
        lifeshack: "Best when you want background help plus tighter control over the pipeline.",
      },
    ],
    faqs: [
      {
        question: "Is LifeShack a JobCopilot alternative?",
        answer:
          "Yes. Both tools sit in the AI job application automation category. JobCopilot emphasizes an automated copilot that applies after setup. LifeShack is the better fit when you want a controlled application workflow with tailored materials and visibility.",
      },
      {
        question: "Who should choose LifeShack over JobCopilot?",
        answer:
          "Choose LifeShack if you want automation but still want to control sensitive companies, pause applications, and understand what is moving through your pipeline.",
      },
    ],
  },
  {
    slug: "loopcv",
    competitor: "LoopCV",
    title: "LifeShack vs LoopCV | AI Auto Apply Comparison",
    metaDescription:
      "Compare LifeShack and LoopCV for AI auto apply, job matching, application tracking, tailored resumes, and job-search automation.",
    eyebrow: "LifeShack vs LoopCV",
    headline: "A better job-search agent for people who want quality and control with their application volume.",
    subheadline:
      "LoopCV centers on auto apply. LifeShack combines automated applications with tailored materials, blocklists, and a guided free trial.",
    competitorSummary:
      "LoopCV lets users upload a CV, set preferences, and automatically find and apply to jobs. It also includes tracking, emails, and CV checks.",
    lifeshackSummary:
      "LifeShack focuses on turning a user profile into targeted applications with tailored materials, clear plan tiers, and controls for where the agent should and should not apply.",
    competitorBestFor: "Job seekers who want broad automation and are comfortable tuning the workflow.",
    lifeshackBestFor: "Job seekers who want a trial that quickly proves whether done-for-you applications can produce momentum.",
    verdict:
      "If broad auto-apply setup is your top priority, LoopCV may fit. If you want a guided application engine with personalization, visibility, and blocklist controls, LifeShack is the better fit.",
    competitorStrengths: [
      "Automation-led positioning.",
      "Job search, tracking, emails, and CV checks.",
      "Category message around automated job applications.",
    ],
    lifeshackAdvantages: [
      "Simple outcome message: stop applying manually and start interviewing.",
      "Current-employer and company blocklists for safer automation.",
      "Free trial helps you see application momentum before choosing a plan.",
    ],
    features: [
      {
        feature: "Core job",
        competitor: "Automated job-search loops that find and apply to matching jobs.",
        lifeshack: "AI agent workflow for matching, tailored applications, submission, and tracking.",
      },
      {
        feature: "Free entry",
        competitor: "Low-friction entry into an auto-apply workflow.",
        lifeshack: `Free trial includes ${FREE_TRIAL_APPLICATION_CREDITS} application credits with no credit card messaging.`,
      },
      {
        feature: "Materials",
        competitor: "CV checks and improvement tools.",
        lifeshack: "AI tailored cover letters and resume tailoring on higher plans.",
      },
      {
        feature: "Control",
        competitor: "Preference and loop configuration.",
        lifeshack: "Search preferences, pause/resume, and company blocklists.",
      },
      {
        feature: "Decision point",
        competitor: "Broad automation-led setup.",
        lifeshack: `Fast activation into a free trial with ${FREE_TRIAL_APPLICATION_CREDITS} application credits, then upgrade after applications are underway.`,
      },
    ],
    faqs: [
      {
        question: "How is LifeShack different from LoopCV?",
        answer:
          "Both tools are in the auto-apply category. LifeShack is the better fit when you want quality-at-scale, tailored applications, and blocklist controls rather than only more application volume.",
      },
      {
        question: "Can LifeShack compete with LoopCV's low-friction entry point?",
        answer:
          "Yes. The LifeShack trial is meant to prove value quickly: set up the agent, watch applications move, and upgrade when the workflow works for you.",
      },
    ],
  },
  {
    slug: "sonara",
    competitor: "Sonara",
    title: "LifeShack vs Sonara | AI Job Search Automation Comparison",
    metaDescription:
      "Compare LifeShack and Sonara for AI job-search automation, auto apply, tailored applications, controls, and free trial access.",
    eyebrow: "LifeShack vs Sonara",
    headline: "Cast a wide net, but keep control of the applications that represent you.",
    subheadline:
      "Sonara markets continuous AI job-search automation. LifeShack offers a more transparent, controlled way to get applications moving with tailored materials and an easy trial.",
    competitorSummary:
      "Sonara positions itself as an AI platform that continuously finds and applies to relevant jobs. Its message leans heavily on application volume and time savings.",
    lifeshackSummary:
      "LifeShack focuses that automation promise around setup clarity, user controls, tailored application materials, and an easy trial path.",
    competitorBestFor: "Job seekers who want a done-for-you application service with a broad automation promise.",
    lifeshackBestFor: "Job seekers who want an AI agent that still gives them visibility, controls, and a trial before committing.",
    verdict:
      "If a managed service model is what you want, Sonara may fit. If you want a clear trial path, tailored materials, and more direct controls over your job-search agent, LifeShack is the better fit.",
    competitorStrengths: [
      "Done-for-you positioning.",
      "Promise around expanding application volume.",
      "Time-saving message for manual-application burnout.",
    ],
    lifeshackAdvantages: [
      "Trial-first offer lowers the barrier to trying the workflow.",
      "Dashboard visibility and plan tiers make the workflow concrete.",
      "Blocklists and pause controls help users trust automation.",
    ],
    features: [
      {
        feature: "Core job",
        competitor: "Continuously find and apply to relevant jobs.",
        lifeshack: "Find, tailor, submit, and track applications through LifeShack's AI agent.",
      },
      {
        feature: "Messaging",
        competitor: "10x your job applications.",
        lifeshack: "Stop applying manually. Start interviewing.",
      },
      {
        feature: "User control",
        competitor: "Managed automation positioning.",
        lifeshack: "Preferences, pause controls, dashboard tracking, and company blocklists.",
      },
      {
        feature: "Materials",
        competitor: "Profile-driven applications.",
        lifeshack: "AI tailored cover letters and resumes as part of the workflow.",
      },
      {
        feature: "Decision point",
        competitor: "Continuous service until hired.",
        lifeshack: `Start the free trial with ${FREE_TRIAL_APPLICATION_CREDITS} application credits, get applications moving, and upgrade when the value is visible.`,
      },
    ],
    faqs: [
      {
        question: "Is LifeShack a Sonara alternative?",
        answer:
          "Yes. Both products sit in the AI job-search automation category. LifeShack is the better fit if you want a trial-first experience and more control over automated applications.",
      },
      {
        question: "How does LifeShack compare with Sonara on control?",
        answer:
          "The biggest difference is control. LifeShack focuses on tailored materials, dashboard visibility, and the ability to pause applications or block companies.",
      },
    ],
  },
  {
    slug: "teal",
    competitor: "Teal",
    title: "LifeShack vs Teal | AI Resume Builder and Auto Apply Comparison",
    metaDescription:
      "Compare LifeShack and Teal for AI resumes, job tracking, application automation, cover letters, and free-trial-led job search.",
    eyebrow: "LifeShack vs Teal",
    headline: "Teal helps you prepare. LifeShack helps you get applications out.",
    subheadline:
      "Teal is a polished resume builder and job tracker. LifeShack is for job seekers who need the next step: relevant applications submitted consistently on their behalf.",
    competitorSummary:
      "Teal focuses on resume building, keyword matching, job tracking, and organizing a search. It fits users who want to keep owning the application process but need a better workspace.",
    lifeshackSummary:
      "LifeShack starts after the resume-tool bottleneck: it uses the user's profile and preferences to tailor materials, submit applications, and track progress.",
    competitorBestFor: "Job seekers who want to build resumes, track jobs, and tailor materials manually.",
    lifeshackBestFor: "Job seekers who already know they need more applications and want an agent to do the repetitive work.",
    verdict:
      "If your main problem is resume organization, Teal may help. If your real bottleneck is not getting enough relevant applications submitted each week, LifeShack is the better fit.",
    competitorStrengths: [
      "Resume builder and job tracker.",
      "Keyword and match-score workflows for manual tailoring.",
      "Clear resume-workflow positioning and premium upgrade path.",
    ],
    lifeshackAdvantages: [
      "Goes beyond preparation into application execution.",
      "Automates repetitive job-search labor.",
      "Lets you try the application workflow instead of staying stuck in another writing tool.",
    ],
    features: [
      {
        feature: "Core job",
        competitor: "Build resumes, tailor content, and track opportunities.",
        lifeshack: "Run the application workflow with AI search, tailoring, submission, and tracking.",
      },
      {
        feature: "Application execution",
        competitor: "Autofill supports the form, then the user reviews and applies.",
        lifeshack: "LifeShack submits applications on behalf of the user.",
      },
      {
        feature: "Best user moment",
        competitor: "A better-organized resume and job tracker.",
        lifeshack: "A dashboard showing applications moving through the pipeline.",
      },
      {
        feature: "Control",
        competitor: "Manual selection and tracking.",
        lifeshack: "Preferences, pause controls, and company blocklists.",
      },
      {
        feature: "Decision point",
        competitor: "Upgrade for more resume and matching power.",
        lifeshack: "Try the agent free and upgrade when the workflow saves real time.",
      },
    ],
    faqs: [
      {
        question: "Can LifeShack replace Teal?",
        answer:
          "It depends on the user's bottleneck. Teal is a resume and organization workspace. LifeShack is the better fit when users want the application process itself handled for them.",
      },
      {
        question: "Who should choose LifeShack over Teal?",
        answer:
          "Choose LifeShack if the bottleneck is not writing one better resume, but consistently getting many relevant, tailored applications submitted.",
      },
    ],
  },
  {
    slug: "jobscan",
    competitor: "Jobscan",
    title: "LifeShack vs Jobscan | ATS Resume Scanner and Auto Apply Comparison",
    metaDescription:
      "Compare LifeShack and Jobscan for ATS resume optimization, AI job-search automation, tailored applications, and free trial access.",
    eyebrow: "LifeShack vs Jobscan",
    headline: "A resume score can help. A submitted application pipeline is the next level.",
    subheadline:
      "Jobscan is built around ATS optimization and resume matching. LifeShack is built for the downstream workflow: find the jobs, tailor the application, submit it, and track what happens.",
    competitorSummary:
      "Jobscan is an ATS resume optimization platform with a review-first Auto Apply workflow. It helps job seekers improve resume fit and approve supported applications before submission.",
    lifeshackSummary:
      "LifeShack is not just a scanner. It is a job-search agent that helps turn a profile into tailored applications and submitted opportunities.",
    competitorBestFor: "Job seekers who prioritize ATS fit and want to approve each supported Auto Apply submission.",
    lifeshackBestFor: "Job seekers who want ATS-aware materials plus help getting applications submitted at scale.",
    verdict:
      "If you only need resume optimization, Jobscan may be enough. If you want the work after optimization handled too, LifeShack is the better fit: matching, tailoring, submitting, and tracking.",
    competitorStrengths: [
      "ATS optimization workflow.",
      "Resume-to-job match reports.",
      "Educational content around applicant tracking systems.",
    ],
    lifeshackAdvantages: [
      "Executes the job-search workflow beyond resume scanning.",
      "Connects tailoring to actual submitted applications.",
      "Free trial can show value through applications, not only a score.",
    ],
    features: [
      {
        feature: "Core job",
        competitor: "Optimize resumes for ATS and job descriptions.",
        lifeshack: "Find jobs, tailor materials, submit applications, and track outcomes.",
      },
      {
        feature: "Output",
        competitor: "Match score, keyword insights, and resume guidance.",
        lifeshack: "Submitted applications and a tracked job-search pipeline.",
      },
      {
        feature: "User effort",
        competitor: "User applies manually after optimizing.",
        lifeshack: "LifeShack handles the repetitive application process.",
      },
      {
        feature: "Best user moment",
        competitor: "Seeing a resume score improve.",
        lifeshack: "Seeing applications submitted while you focus on higher-value work.",
      },
      {
        feature: "Decision point",
        competitor: "Scan your resume for free.",
        lifeshack: `Start a free trial with ${FREE_TRIAL_APPLICATION_CREDITS} application credits and get the job-search agent working.`,
      },
    ],
    faqs: [
      {
        question: "Is LifeShack an ATS scanner like Jobscan?",
        answer:
          "LifeShack overlaps with ATS-aware tailoring, but it solves a broader problem. It is about getting applications submitted, not only scoring a resume.",
      },
      {
        question: "Who should choose LifeShack over Jobscan?",
        answer:
          "Choose LifeShack when the goal is a running application pipeline. Jobscan fits better when the goal is to manually optimize a resume before applying yourself.",
      },
    ],
  },
];

const profileComparisonPages: ComparisonPage[] = competitorProfiles.map((page) => ({
  slug: page.slug,
  ...(page.relatedComparisonSlugs
    ? { relatedComparisonSlugs: page.relatedComparisonSlugs }
    : {}),
  competitor: page.name,
  title: page.metaTitle,
  metaDescription: page.metaDescription,
  eyebrow: page.title,
  headline: `${page.title}: choose the workflow that fits your job search.`,
  subheadline: page.verdict,
  competitorSummary: page.competitorSummary,
  lifeshackSummary: page.lifeShackSummary,
  competitorBestFor: page.bestFit,
  lifeshackBestFor: lifeShackProfile.bestFit,
  competitorLimitation: page.limitation,
  lifeshackLimitation: lifeShackProfile.limitation,
  verdict: page.verdict,
  competitorStrengths: page.chooseCompetitor,
  lifeshackAdvantages: page.chooseLifeShack,
  features: page.tableRows.map((row) => ({
    feature: row.label,
    competitor: row.competitor,
    lifeshack: row.lifeShack,
  })),
  faqs: page.faqs,
  pricingNote: page.pricingSummary,
  reviewedAt: page.verifiedAt,
  sources: page.sources,
  analysisSections: page.sections,
}));

export const comparisonPages: ComparisonPage[] = [
  ...profileComparisonPages,
  ...baseComparisonPages.filter(
    (page) =>
      !profileComparisonPages.some(
        (profilePage) => profilePage.slug === page.slug
      )
  ),
];

export function getComparisonBySlug(slug: string): ComparisonPage | undefined {
  return comparisonPages.find((page) => page.slug === slug);
}
